import axios from 'axios';
import { authenticatedApiClient } from './authService';
import {
    PreqinSearchParams,
    PreqinQuickSearchParams,
    PreqinFundPortfolioParams,
    PreqinBuyoutParams,
    PreqinApiResponse,
    PreqinDealData,
    PreqinCategories
} from '../types/preqin';

import { PREQIN_API_URL } from '../config/api';

// Cache for IDs to avoid repeated API calls
const idsCache = new Map<string, number[]>();

// Create axios instance for Preqin API with custom base URL
const preqinApiClient = axios.create({
    baseURL: PREQIN_API_URL,
});

// Use the centralized auth interceptor by copying it to this client
preqinApiClient.interceptors.request = authenticatedApiClient.interceptors.request;
preqinApiClient.interceptors.response = authenticatedApiClient.interceptors.response;

export const preqinService = {
    // Get Preqin data with query parameters
    getPreqinData: async (params: PreqinSearchParams): Promise<PreqinApiResponse> => {
        try {
            const queryString = new URLSearchParams(params as any).toString();
            const response = await preqinApiClient.get(`/pregin_get?${queryString}`);
            return response.data as PreqinApiResponse;
        } catch (error) {
            console.error('Error fetching Preqin data:', error);
            throw error;
        }
    },

    // Quick search functionality
    quickSearch: async (params: PreqinQuickSearchParams): Promise<PreqinApiResponse> => {
        try {
            const searchParams = new URLSearchParams();
            searchParams.append('term', params.query || '');
            searchParams.append('size', '40');
            searchParams.append('category', 'investor');//,asset

            const response = await preqinApiClient.get(`/quicksearch?${searchParams.toString()}`);
            return response.data as PreqinApiResponse;
        } catch (error) {
            console.error('Error in Preqin quick search:', error);
            throw error;
        }
    },

    // Get asset deals
    getAssetDeals: async (params: PreqinSearchParams): Promise<PreqinApiResponse> => {
        try {
            const queryString = new URLSearchParams(params as any).toString();
            const response = await preqinApiClient.get(`/get_deals?${queryString}`);
            return response.data as PreqinApiResponse;
        } catch (error) {
            console.error('Error fetching asset deals:', error);
            throw error;
        }
    },

    // Get fund portfolio
    getFundPortfolio: async (investorId: string | number, page = 1): Promise<PreqinApiResponse> => {
        const params = {
            columns: [
                "id",
                "basic.fundManager.id",
                "investors.currency",
                "geographicRegions",
                "name",
                "basic.fundManager.name",
                "basic.strategy",
                "geographicFocus.mainFundFocus",
                "fundRaising.currency",
                "fundRaising.fundSize.localMn",
                "investors.committedAmount.localMn",
                "privateCapital.vintage",
                "basic.fundManager.experience",
                "industryFocus.coreIndustries",
                "industryFocus.verticals",
                "structure.domicile"
            ],
            page: page,
            size: 20,
            sortBy: "privateCapital.vintage",
            sortOrder: "descending",
            category: "Fund",
            newFilters: [
                {
                    id: "investors.id",
                    type: "Aggregate",
                    filterOperation: "AND",
                    nestedPath: "investors",
                    filterBy: [
                        {
                            id: "investors.id",
                            type: "integer",
                            filterBy: investorId
                        }
                    ]
                },
                {
                    id: "basic.assetClasses",
                    type: "LIST",
                    filterBy: ["PE"]
                }
            ],
            view: "investorProfile"
        };

        try {
            const response = await preqinApiClient.post(`/get_fund_portfolio`, params);
            return response.data as PreqinApiResponse;
        } catch (error) {
            console.error('Error fetching fund portfolio:', error);
            throw error;
        }
    },

    // Get buyout deals
    getBuyoutDeals: async (investorId: string | number, page = 1): Promise<PreqinApiResponse<PreqinDealData>> => {
        try {
            const cacheKey = `buyout_${investorId}`;
            let ids: number[] = [];
            
            // Check cache first
            if (idsCache.has(cacheKey)) {
                ids = idsCache.get(cacheKey)!;
            } else {
                // First get buyout IDs using the deals API params
                const dealApiParams = {
                    "columns": [
                        "id"
                    ],
                    "page": 1,
                    "size": 10000,
                    "category": "Fund",
                    "newFilters": [
                        {
                            "id": "investors.id",
                            "type": "integer",
                            "nestedPath": "investors",
                            "filterBy": investorId
                        },
                        {
                            "id": "basic.assetClasses",
                            "type": "LIST",
                            "filterBy": [
                                "PE"
                            ]
                        }
                    ],
                    "view": "investorProfile"
                }

                const idsResponse = await preqinApiClient.post(`/get_buyout_ids`, dealApiParams);
                const responseData = idsResponse.data as any;
                ids = responseData?.data?.map((item: any) => parseInt(item.id, 10)) || [];
                
                // Cache the IDs
                idsCache.set(cacheKey, ids);
            }

            if (ids.length === 0) {
                return { 
                    data: [], 
                    meta: { 
                        returned: 0, 
                        total: 0, 
                        page: 1, 
                        took: 0, 
                        aggregations: {}, 
                        filterCounts: null 
                    } 
                };
            }

            // Then get deals using those IDs
            const dealParams = {
                columns: [
                    "id",
                    "pE.portfolioCompany.id",
                    "pE.portfolioCompany.name",
                    "buyers.investor.name",
                    "pE.portfolioCompany.primaryAddress.country",
                    "basic.dealDate",
                    "basic.currency",
                    "basic.dealSize.localMn",
                    "pE.portfolioCompany.primaryIndustry.industry",
                    "pE.portfolioCompany.verticals.text",
                    "basic.dealStatus",
                    "pE.portfolioCompany.status",
                    "buyers.leadPartners.text",
                    "buyers.boardMembers.text",
                    "pE.dealDetails.investmentType",
                    "pE.portfolioCompany.primaryAddress.geographicRegions"
                ],
                page: page,
                size: 20,
                sortBy: "basic.dealDate",
                sortOrder: "descending",
                category: "Deal",
                newFilters: [
                    {
                        id: "buyerFunds.fundId",
                        type: "LIST",
                        filterBy: ids
                    },
                    {
                        id: "assetClasses",
                        type: "list",
                        filterBy: ["PE"]
                    }
                ],
                view: "investorProfile"
            };

            const response = await preqinApiClient.post(`/get_buyout_deals`, dealParams);
            return response.data as PreqinApiResponse<PreqinDealData>;
        } catch (error) {
            console.error('Error fetching buyout deals:', error);
            throw error;
        }
    },

    // Get venture capital deals
    getVentureCapitalDeals: async (investorId: string | number, page = 1): Promise<PreqinApiResponse<PreqinDealData>> => {
        try {
            const cacheKey = `vc_${investorId}`;
            let ids: number[] = [];
            
            // Check cache first
            if (idsCache.has(cacheKey)) {
                ids = idsCache.get(cacheKey)!;
            } else {
                // First get IDs using the deals API params (reusing buyout IDs endpoint)
                const dealApiParams = {
                    "columns": [
                        "id"
                    ],
                    "page": 1,
                    "size": 10000,
                    "category": "Fund",
                    "newFilters": [
                        {
                            "id": "investors.id",
                            "type": "integer",
                            "nestedPath": "investors",
                            "filterBy": investorId
                        },
                        {
                            "id": "basic.assetClasses",
                            "type": "LIST",
                            "filterBy": [
                                "VC"
                            ]
                        }
                    ]
                }

                const idsResponse = await preqinApiClient.post(`/get_buyout_ids`, dealApiParams);
                const responseData = idsResponse.data as any;
                ids = responseData?.data?.map((item: any) => parseInt(item.id, 10)) || [];
                
                // Cache the IDs
                idsCache.set(cacheKey, ids);
            }

            if (ids.length === 0) {
                return { 
                    data: [], 
                    meta: { 
                        returned: 0, 
                        total: 0, 
                        page: 1, 
                        took: 0, 
                        aggregations: {}, 
                        filterCounts: null 
                    } 
                };
            }

            // Then get VC deals using those IDs
            const dealParams = {
                columns: [
                    "id",
                    "pE.portfolioCompany.id",
                    "pE.portfolioCompany.name",
                    "buyers.investor.name",
                    "pE.portfolioCompany.primaryAddress.country",
                    "basic.dealDate",
                    "basic.currency",
                    "basic.dealSize.localMn",
                    "pE.portfolioCompany.primaryIndustry.industry",
                    "pE.portfolioCompany.verticals.text",
                    "basic.dealStatus",
                    "pE.portfolioCompany.status",
                    "buyers.leadPartners.text",
                    "buyers.boardMembers.text",
                    "pE.dealDetails.stage",
                    "pE.portfolioCompany.primaryAddress.geographicRegions"
                ],
                page: page,
                size: 20,
                sortBy: "basic.dealDate",
                sortOrder: "descending",
                category: "Deal",
                newFilters: [
                    {
                        id: "buyerFunds.fundId",
                        type: "LIST",
                        filterBy: ids
                    },
                    {
                        id: "assetClasses",
                        type: "list",
                        filterBy: ["VC"]
                    },
                    {
                        id: "basic.dealDate",
                        type: "DATERANGE",
                        filterBy: {
                            min: null,
                            max: null
                        },
                        filterOperation: null
                    }
                ],
                view: "investorProfile"
            };

            const response = await preqinApiClient.post(`/get_buyout_deals`, dealParams);
            return response.data as PreqinApiResponse<PreqinDealData>;
        } catch (error) {
            console.error('Error fetching venture capital deals:', error);
            throw error;
        }
    },

    // Post to Preqin API
    postPreqinData: async (params: any): Promise<PreqinApiResponse> => {
        try {
            const response = await preqinApiClient.post(`/pregin_get`, params);
            return response.data as PreqinApiResponse;
        } catch (error) {
            console.error('Error posting to Preqin API:', error);
            throw error;
        }
    }
};

// Export categories enum for use in components
export { PreqinCategories };
export default preqinService;